import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ScanChecklistModal from "@/components/ScanChecklistModal";

/**
 * ScanSheetLauncher — coach dashboard tile that lets a coach scan any player's
 * printed sheet without leaving the dashboard. Two steps:
 *   1. Pick a player from a searchable list (loads once)
 *   2. Open the shared ScanChecklistModal for that player
 */
export default function ScanSheetLauncher() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null); // {id, name, current_level}

  useEffect(() => {
    if (!pickerOpen || players.length) return;
    api.get("/students").then((r) => setPlayers(r.data || [])).catch(() => setPlayers([]));
  }, [pickerOpen, players.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players.slice(0, 20);
    return players
      .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [players, query]);

  return (
    <>
      <button
        onClick={() => setPickerOpen(true)}
        className="w-full h-full p-4 border border-[#262626] hover:border-[#CCFF00] transition-all group text-left"
        data-testid="scan-sheet-tile"
      >
        <Camera strokeWidth={1.75} className="w-4 h-4 mb-2 text-[#CCFF00]" />
        <div className="text-xs font-bold uppercase tracking-wider leading-tight group-hover:text-[#CCFF00] transition-colors">
          Scan sheet
        </div>
        <div className="text-[10px] text-[#525252] mt-1 leading-tight">
          Snap a photo of any player's printed sheet — Claude reads the ticks
        </div>
      </button>

      {/* Player picker — portaled to body so ancestor transforms
          (e.g. .fade-up on dashboard grid) don't scope our fixed backdrop
          inside the tile's cell. */}
      {pickerOpen && !chosen && createPortal((
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPickerOpen(false)}
          data-testid="scan-picker-modal"
        >
          <div
            className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#262626]">
              <div className="text-sm font-medium mb-1">Pick a player to scan for</div>
              <input
                autoFocus
                type="text"
                placeholder="Search by name or email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-xs text-[#E5E5E5] focus:outline-none focus:border-[#CCFF00]"
                data-testid="scan-picker-search"
              />
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-[#141414]">
              {filtered.length === 0 && (
                <div className="p-4 text-xs text-[#A3A3A3]">
                  {players.length === 0 ? "Loading players…" : "No players match that search."}
                </div>
              )}
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setChosen({ id: p.id, name: p.name, current_level: p.assessment_level?.current || null })}
                  className="w-full text-left px-4 py-3 hover:bg-[#141414] transition-colors flex items-center justify-between gap-2"
                  data-testid={`scan-picker-player-${p.id}`}
                >
                  <div>
                    <div className="text-xs text-[#E5E5E5]">{p.name}</div>
                    <div className="text-[10px] text-[#525252]">
                      {p.email}
                      {p.assessment_level?.current ? ` · L${p.assessment_level.current}` : " · no level yet"}
                    </div>
                  </div>
                  <ArrowRight className="w-3 h-3 text-[#525252]" />
                </button>
              ))}
            </div>
            <div className="border-t border-[#262626] px-4 py-2 text-right">
              <button
                onClick={() => setPickerOpen(false)}
                className="text-xs px-3 py-1.5 text-[#A3A3A3] hover:text-[#E5E5E5]"
                data-testid="scan-picker-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Once a player is picked, the shared scan modal takes over */}
      <ScanChecklistModal
        open={!!chosen}
        onClose={() => { setChosen(null); setPickerOpen(false); }}
        studentId={chosen?.id || ""}
        studentName={chosen?.name || ""}
        currentLevel={chosen?.current_level || null}
      />
    </>
  );
}
